import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CloudSun, CloudRain, Wind, CheckCircle2, Clock } from "lucide-react";

export function useThreatSummary() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/threat-summary").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 600_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

const issuedText = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }) + " ET";
};

// Pick a readable icon per "what's happening" line from its wording.
const lineIcon = (t = "") => {
  const s = t.toLowerCase();
  if (/(rain|shower|storm|thunder|wet)/.test(s)) return CloudRain;
  if (/(wind|breez|gust)/.test(s)) return Wind;
  return CloudSun;
};

export function ThreatSummarySlide({ summary }) {
  if (!summary?.available || !summary.headline) return null;
  const todo = summary.todo || [];
  const happening = summary.happening || [];
  return (
    <div data-testid="threat-summary-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(140deg, #13284a 0%, #0c1830 55%, #0a0f1c 100%)" }}>
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 2px, transparent 2px 28px)" }} />
      <div className="relative flex h-full w-full flex-col px-[54px] py-[30px]">
        {/* Header */}
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 rounded-full bg-[#2f7bed] px-4 py-1.5 font-oswald text-[20px] font-black uppercase tracking-[0.2em] text-white">
            <CloudSun className="h-5 w-5" strokeWidth={2.6} /> The Forecast · In Plain Words
          </span>
          {summary.period && <span className="rounded-full bg-[#ffd60a] px-4 py-1.5 font-oswald text-[20px] font-black uppercase tracking-wide text-[#0a0f1c]">{summary.period}</span>}
        </div>
        <div className="mt-3 font-oswald text-[64px] font-black uppercase leading-[0.92] text-white">{summary.headline}</div>

        {/* What's happening */}
        <div className="mt-6 flex flex-1 flex-col justify-center gap-3">
          {happening.map((h, i) => {
            const Icon = lineIcon(h);
            return (
              <div key={i} className="flex items-center gap-4 rounded-[12px] bg-white/[0.06] px-5 py-4 ring-1 ring-white/10" data-testid={`summary-happening-${i}`}>
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[10px] bg-[#2f7bed]/25">
                  <Icon className="h-8 w-8 text-[#7fb2ff]" strokeWidth={2.2} />
                </span>
                <span className="font-plex text-[34px] font-semibold leading-snug text-white">{h}</span>
              </div>
            );
          })}
        </div>

        {/* What to do */}
        {todo.length > 0 && (
          <div className="mt-4 rounded-[12px] bg-[#1fae6a]/18 p-5 ring-1 ring-[#1fae6a]/45">
            <div className="mb-2.5 font-oswald text-[25px] font-black uppercase tracking-wide text-[#4fe39a]">What To Do</div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
              {todo.map((t, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`summary-todo-${i}`}>
                  <CheckCircle2 className="h-7 w-7 shrink-0 text-[#4fe39a]" strokeWidth={2.4} />
                  <span className="font-plex text-[28px] font-semibold leading-snug text-white">{t}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 font-plex text-[19px] font-medium tracking-wide text-white/55">
          <Clock className="h-5 w-5" /> From the {summary.office} forecast discussion · {issuedText(summary.issued)}
        </div>
      </div>
    </div>
  );
}
